import { Box, Typography, Grid, Chip, Card, CardContent } from '@mui/material';
import { useClientContext } from '../context/ClientContextProvider';

const Home = () => {
  const { currentProperty, loading } = useClientContext();
  
  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography 
          variant="h6"
          sx={{ 
            color: 'rgba(16, 29, 59, 0.6)',
            fontSize: '16px'
          }}
        >
          Loading client data...
        </Typography>
      </Box>
    );
  }

  if (!currentProperty) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography 
          variant="h6"
          sx={{ 
            color: '#101D3B',
            fontSize: '18px',
            fontWeight: 700
          }}
        >
          No property selected
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            mt: 2,
            color: 'rgba(16, 29, 59, 0.6)',
            fontSize: '14px'
          }}
        >
          Please select a property from the menu.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3, mb: 8 }}>
      <Card sx={{ 
        mb: 4, 
        overflow: 'hidden', 
        borderRadius: '16px',
        border: '1px solid rgba(16, 29, 59, 0.2)',
        boxShadow: '0px 8px 32px -4px rgba(16, 29, 59, 0.1)'
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box 
            sx={{ 
              bgcolor: '#00AB9B', 
              py: 2, 
              px: 3 
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                color: '#FFFFFF !important',
                fontSize: '18px'
              }}
            >
              Property Information
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" sx={{ mb: 2, color: '#101D3B' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>Address:</Box> 
                  {currentProperty.address}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: '#101D3B' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>City:</Box> 
                  {currentProperty.city}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: '#101D3B' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>State:</Box> 
                  {currentProperty.state}
                </Typography>
                <Typography variant="body1" sx={{ color: '#101D3B' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>Zip Code:</Box> 
                  {currentProperty.zipCode}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" sx={{ mb: 2, color: '#101D3B' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>Contact:</Box> 
                  {currentProperty.contactFullName}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: '#101D3B' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>Email:</Box> 
                  {currentProperty.contactEmail}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: '#101D3B' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>Phone:</Box> 
                  {currentProperty.contactPhoneNumber}
                </Typography>
                <Box sx={{ color: '#101D3B' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>Status:</Box>
                  <Chip 
                    label={currentProperty.status.charAt(0).toUpperCase() + currentProperty.status.slice(1)} 
                    size="small" 
                    sx={{ 
                      ml: 1, 
                      fontWeight: 500,
                      height: '24px',
                      display: 'inline-flex',
                      position: 'relative',
                      top: '-1px',
                      bgcolor: currentProperty.status === 'active' ? 'rgba(0, 171, 155, 0.1)' : 'rgba(16, 29, 59, 0.1)',
                      color: currentProperty.status === 'active' ? '#00AB9B' : 'rgba(16, 29, 59, 0.6)',
                      border: '1px solid',
                      borderColor: currentProperty.status === 'active' ? '#00AB9B' : 'rgba(16, 29, 59, 0.2)',
                      '& .MuiChip-label': {
                        color: currentProperty.status === 'active' ? '#00AB9B' : 'rgba(16, 29, 59, 0.6)',
                        padding: '0 8px'
                      }
                    }} 
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ 
        mb: 4, 
        overflow: 'hidden', 
        borderRadius: '16px',
        border: '1px solid rgba(16, 29, 59, 0.2)',
        boxShadow: '0px 8px 32px -4px rgba(16, 29, 59, 0.1)'
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box 
            sx={{ 
              bgcolor: '#101D3B', 
              py: 2, 
              px: 3 
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                color: '#FFFFFF !important',
                fontSize: '18px'
              }}
            >
              Insurance Requirements for Working on Site
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" sx={{ mb: 2, color: '#101D3B' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>Commercial General Liability:</Box> 
                  {currentProperty.commercialGeneralLiabilityAmount 
                    ? `$${currentProperty.commercialGeneralLiabilityAmount.toLocaleString()}`
                    : 'Not specified'}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: '#101D3B' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>Workers Compensation:</Box> 
                  {currentProperty.workersCompensationAmount 
                    ? `$${currentProperty.workersCompensationAmount.toLocaleString()}`
                    : 'Not specified'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" sx={{ mb: 2, color: '#101D3B' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>Automobile Liability:</Box> 
                  {currentProperty.automobileLiabilityAmount 
                    ? `$${currentProperty.automobileLiabilityAmount.toLocaleString()}`
                    : 'Not specified'}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: '#101D3B' }}>
                  <Box component="span" sx={{ fontWeight: 700, mr: 1 }}>Umbrella:</Box> 
                  {currentProperty.umbrellaAmount 
                    ? `$${currentProperty.umbrellaAmount.toLocaleString()}`
                    : 'Not specified'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Add more dashboard content here if needed */}
      </Grid>
    </Box>
  );
};

export default Home; 